import styled, { createGlobalStyle } from "styled-components";

/* Match the admin page dark backgrounds to the CSD app's dark mode */
export const AdminGlobalStyle = createGlobalStyle`
	#dark body {
		background-color: var(--bg-d2);
	}
	#light body {
		background-color: var(--bg-lg);
	}
	#dark .top-navigation {
		background-color: var(--bg-d1);
		border-bottom: 1px solid var(--border-subtle, rgba(140, 150, 170, 0.16));
	}
`;

export const AdminConsoleEl = styled.div`
	--admin-border: var(--border-subtle, rgba(140, 150, 170, 0.16));

	display: flex;
	gap: 24px;
	align-items: flex-start;
	max-width: 1200px;
	margin: 0 auto;
	padding: 96px 32px 80px 32px;
	color: var(--txt-l);
	box-sizing: border-box;

	* {
		box-sizing: border-box;
	}

	/* ---------- Left nav ---------- */
	.admin-nav {
		position: sticky;
		top: 96px;
		flex: 0 0 240px;
		background: var(--bg-d2);
		border: 1px solid var(--admin-border);
		border-radius: 12px;
		padding: 10px;
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.admin-nav-title {
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 1px;
		text-transform: uppercase;
		color: var(--txt-lg);
		padding: 8px 10px 6px 10px;
	}

	.admin-nav-item {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		width: 100%;
		text-align: left;
		background: transparent;
		border: none;
		border-radius: 8px;
		padding: 9px 12px;
		font-size: 13px;
		font-weight: 500;
		color: var(--txt-lg);
		cursor: pointer;
		transition: background 0.12s ease, color 0.12s ease;

		&:hover {
			background: var(--bg-dg);
			color: var(--txt-l);
		}

		&.active {
			background: var(--highlight-blue, rgba(31, 133, 222, 0.15));
			color: var(--txt-l);
			box-shadow: inset 2px 0 0 var(--blue);
		}

		.count {
			font-size: 10px;
			font-weight: 700;
			color: var(--txt-lg);
			background: var(--bg-d0-dash);
			border-radius: 10px;
			padding: 1px 7px;
		}
	}

	.admin-nav-add {
		margin-top: 6px;
		border-top: 1px solid var(--admin-border);
		padding-top: 8px;
	}

	.admin-nav-add button {
		width: 100%;
		background: transparent;
		border: 1px dashed var(--admin-border);
		border-radius: 8px;
		padding: 9px 12px;
		font-size: 12px;
		font-weight: 600;
		color: var(--txt-lg);
		cursor: pointer;
		transition: border-color 0.12s ease, color 0.12s ease;

		&:hover {
			border-color: var(--blue);
			color: var(--blue);
		}
	}

	/* ---------- Right panel ---------- */
	.admin-panel {
		flex: 1 1 auto;
		min-width: 0;
		background: var(--bg-d2);
		border: 1px solid var(--admin-border);
		border-radius: 12px;
	}

	.admin-panel-head {
		padding: 22px 26px 18px 26px;
		border-bottom: 1px solid var(--admin-border);
	}

	.admin-panel-head h1 {
		font-size: 22px;
		font-weight: 700;
		color: var(--txt-l);
		margin: 0;
		padding: 0;
		font-family: inherit;
		letter-spacing: 0;
		text-transform: none;
	}

	.admin-panel-head .desc {
		font-size: 13px;
		color: var(--txt-lg);
		margin-top: 6px;
	}

	.admin-panel-body {
		padding: 22px 26px;
		max-height: 70vh;
    	overflow-y: auto;
	}

	.admin-section-label {
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.6px;
		text-transform: uppercase;
		color: var(--txt-lg);
		margin: 0 0 10px 0;
	}

	.admin-help {
		font-size: 12px;
		color: var(--txt-lg);
		margin: -4px 0 14px 0;
		line-height: 1.5;
	}

	/* ---------- Inputs ---------- */
	.admin-input,
	.admin-textarea,
	.admin-select {
		width: 100%;
		background: var(--bg-d0-dash);
		color: var(--txt-l);
		border: 1px solid var(--admin-border);
		border-radius: 8px;
		padding: 10px 12px;
		font-size: 13px;
		outline: none;
		transition: border-color 0.12s ease;
		font-family: inherit;

		&:focus {
			border-color: var(--blue);
		}
		&::placeholder {
			color: var(--txt-lg);
		}
		&:read-only,
		&:disabled {
			opacity: 0.6;
			cursor: not-allowed;
		}
	}

	.admin-textarea {
		min-height: 120px;
		resize: vertical;
		line-height: 1.5;
	}

	.admin-select {
		appearance: none;
		cursor: pointer;
		max-width: 360px;
	}

	.admin-select-row {
		display: flex;
		align-items: center;
		gap: 12px;
		flex-wrap: wrap;
		margin: 0 0 16px 0;
	}

	.admin-select-row label {
		flex: 0 0 auto;
		font-size: 12px;
		font-weight: 700;
		color: var(--txt-lg);
	}

	.admin-select-row .admin-select {
		flex: 1 1 220px;
	}

	.admin-textarea.mono {
		font-family: "SFMono-Regular", "Menlo", "Consolas", monospace;
		font-size: 12px;
		min-height: 320px;
		white-space: pre;
	}

	.admin-field {
		margin-bottom: 18px;
	}

	.admin-field > label {
		display: block;
		font-size: 12px;
		font-weight: 600;
		color: var(--txt-lg);
		margin-bottom: 6px;
	}

	/* ---------- Tag chips ---------- */
	.tag-chips {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		margin-bottom: 16px;
	}

	.tag-chip {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		background: var(--bg-dg);
		border: 1px solid var(--admin-border);
		border-radius: 20px;
		padding: 5px 6px 5px 12px;
		font-size: 12px;
		color: var(--txt-l);
	}

	.tag-chip.heading {
		background: transparent;
		border: none;
		font-weight: 800;
		text-transform: uppercase;
		letter-spacing: 0.6px;
		color: var(--txt-lg);
		padding-left: 2px;
		width: 100%;
	}

	.tag-chip .remove {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 18px;
		height: 18px;
		border-radius: 50%;
		border: none;
		background: var(--bg-d0-dash);
		color: var(--txt-lg);
		font-size: 12px;
		line-height: 1;
		cursor: pointer;
		transition: background 0.12s ease, color 0.12s ease;

		&:hover {
			background: var(--red);
			color: #fff;
		}
	}

	.tag-add {
		display: flex;
		gap: 8px;
		max-width: 360px;
	}

	/* ---------- Department rows ---------- */
	.dept-list {
		display: flex;
		flex-direction: column;
		gap: 8px;
		margin-bottom: 14px;
	}

	.dept-row {
		display: flex;
		align-items: center;
		gap: 10px;
		background: var(--bg-d0-dash);
		border: 1px solid var(--admin-border);
		border-radius: 10px;
		padding: 8px 10px;
	}

	.dept-row .swatch {
		flex: 0 0 auto;
		width: 26px;
		height: 26px;
		border-radius: 50%;
		border: 1px solid var(--admin-border);
		padding: 0;
		cursor: pointer;
		background: transparent;
		overflow: hidden;
	}

	.dept-row .swatch input[type="color"] {
		opacity: 0;
		width: 100%;
		height: 100%;
		border: none;
		cursor: pointer;
	}

	.dept-row .dept-name {
		flex: 1 1 auto;
		background: transparent;
		border: none;
		color: var(--txt-l);
		font-size: 13px;
		font-weight: 600;
		outline: none;
		min-width: 0;
	}

	.dept-row .dept-delete {
		flex: 0 0 auto;
		background: transparent;
		border: none;
		color: var(--txt-lg);
		font-size: 16px;
		cursor: pointer;
		padding: 4px 8px;
		border-radius: 6px;
		transition: background 0.12s ease, color 0.12s ease;

		&:hover {
			background: var(--highlight-red, rgba(244, 72, 91, 0.15));
			color: var(--red);
		}
	}

	.inline-add {
		background: transparent;
		border: 1px dashed var(--admin-border);
		border-radius: 8px;
		padding: 9px 14px;
		font-size: 12px;
		font-weight: 600;
		color: var(--txt-lg);
		cursor: pointer;
		transition: border-color 0.12s ease, color 0.12s ease;

		&:hover {
			border-color: var(--blue);
			color: var(--blue);
		}
	}

	/* ---------- Editor mode toggle (visual / text) ---------- */
	.editor-mode-toggle {
		display: inline-flex;
		background: var(--bg-d0-dash);
		border: 1px solid var(--admin-border);
		border-radius: 8px;
		padding: 3px;
		margin-bottom: 18px;
	}

	.editor-mode-toggle .seg {
		background: transparent;
		border: none;
		color: var(--txt-lg);
		font-size: 12px;
		font-weight: 600;
		padding: 6px 18px;
		border-radius: 6px;
		cursor: pointer;
		transition: background 0.12s ease, color 0.12s ease;

		&.active {
			background: var(--blue);
			color: #fff;
		}
	}

	/* ---------- Nested editors (copy / tests) ---------- */
	.nested-group {
		border: 1px solid var(--admin-border);
		border-radius: 10px;
		padding: 14px;
		margin-bottom: 14px;
		background: var(--bg-d1);
	}

	.nested-group-head {
		display: flex;
		align-items: center;
		gap: 8px;
		margin-bottom: 12px;
	}

	.nested-group-head .dept-delete,
	.nested-card-head .dept-delete {
		flex: 0 0 auto;
		background: transparent;
		border: none;
		color: var(--txt-lg);
		font-size: 16px;
		cursor: pointer;
		padding: 4px 8px;
		border-radius: 6px;
		transition: background 0.12s ease, color 0.12s ease;

		&:hover {
			background: var(--highlight-red, rgba(244, 72, 91, 0.15));
			color: var(--red);
		}
	}

	.group-name {
		flex: 1 1 auto;
		background: transparent;
		border: none;
		border-bottom: 1px solid var(--admin-border);
		color: var(--txt-l);
		font-size: 14px;
		font-weight: 700;
		padding: 4px 2px;
		outline: none;
		min-width: 0;

		&:focus {
			border-bottom-color: var(--blue);
		}
	}

	.nested-card {
		border: 1px solid var(--admin-border);
		border-radius: 8px;
		padding: 12px 14px;
		margin-bottom: 10px;
		background: var(--bg-d2);
	}

	.nested-card-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		margin-bottom: 10px;
	}

	.nested-card-title {
		font-size: 13px;
		font-weight: 700;
		color: var(--txt-l);
	}

	.nested-card .admin-field {
		margin-bottom: 12px;
	}

	.admin-tabs {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		margin: 0 0 14px 0;
	}

	.admin-tabs.secondary {
		margin-top: 2px;
	}

	.admin-tabs.tertiary {
		margin-top: -2px;
		margin-bottom: 18px;
	}

	.admin-tab {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		min-height: 32px;
		background: var(--bg-d0-dash);
		border: 1px solid var(--admin-border);
		border-radius: 8px;
		color: var(--txt-lg);
		cursor: pointer;
		font-size: 12px;
		font-weight: 700;
		padding: 6px 12px;
		transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;

		&:hover {
			border-color: var(--blue);
			color: var(--txt-l);
		}

		&.active {
			background: var(--blue);
			border-color: var(--blue);
			color: #fff;
		}

		.count {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			min-width: 18px;
			height: 18px;
			border-radius: 999px;
			background: rgba(255, 255, 255, 0.12);
			color: inherit;
			font-size: 10px;
			padding: 0 6px;
		}
	}

	.admin-tabs.secondary .admin-tab {
		min-height: 30px;
		font-size: 11px;
	}

	.admin-tabs.tertiary .admin-tab {
		min-height: 28px;
		font-size: 11px;
		font-weight: 600;
	}

	.copy-group-summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		border-top: 1px solid var(--admin-border);
		padding: 14px 0 12px 0;
		margin-top: 4px;
	}

	.copy-group-title,
	.section-title {
		font-size: 14px;
		font-weight: 800;
		color: var(--txt-l);
	}

	.copy-group-meta,
	.section-count {
		flex: 0 0 auto;
		font-size: 11px;
		font-weight: 700;
		color: var(--txt-lg);
		background: var(--bg-d0-dash);
		border: 1px solid var(--admin-border);
		border-radius: 999px;
		padding: 3px 9px;
	}

	.copy-entry-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
		gap: 12px;
	}

	.copy-category-card {
		min-width: 0;
		border: 1px solid var(--admin-border);
		border-radius: 10px;
		background: var(--bg-d1);
		padding: 12px;
	}

	.copy-category-card .nested-card {
		margin-bottom: 0;
	}

	.admin-help.compact {
		margin-top: 12px;
		margin-bottom: 0;
	}

	.test-section-group .nested-group-head {
		justify-content: space-between;
		border-bottom: 1px solid var(--admin-border);
		padding-bottom: 10px;
	}

	/* ---------- Buttons / save bar ---------- */
	.admin-save-bar {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 12px;
		padding: 16px 26px;
		border-top: 1px solid var(--admin-border);
		background: var(--bg-d1);
	}

	.admin-save-bar .saved-note {
		margin-right: auto;
		font-size: 12px;
		color: var(--green);
	}

	.btn-save {
		background: var(--blue);
		color: #fff;
		border: 1px solid var(--blue);
		border-radius: 8px;
		padding: 9px 22px;
		font-size: 13px;
		font-weight: 700;
		cursor: pointer;
		transition: background 0.12s ease;

		&:hover {
			background: var(--darkblue);
		}
		&:disabled {
			opacity: 0.5;
			cursor: not-allowed;
		}
	}

	.btn-ghost {
		background: transparent;
		color: var(--txt-lg);
		border: 1px solid var(--admin-border);
		border-radius: 8px;
		padding: 9px 18px;
		font-size: 13px;
		font-weight: 600;
		cursor: pointer;

		&:hover {
			color: var(--txt-l);
			border-color: var(--txt-lg);
		}
	}

	/* ---------- Light theme ---------- */
	#light & {
		--admin-border: rgba(40, 50, 70, 0.12);
		color: var(--txt-dg);

		.admin-nav,
		.admin-panel {
			background: #fff;
		}
		.admin-panel-head h1 {
			color: var(--txt-d);
		}
		.admin-nav-item.active {
			color: var(--txt-d);
		}
		.admin-input,
		.admin-textarea,
		.dept-row,
		.tag-chip {
			background: var(--bg-l1);
			color: var(--txt-dg);
		}
		.dept-row .dept-name,
		.admin-input,
		.admin-textarea,
		.admin-select {
			color: var(--txt-dg);
		}
		.admin-nav-item .count,
		.tag-chip .remove {
			background: var(--bg-l2);
		}
		.admin-save-bar {
			background: var(--bg-lg);
		}
		.editor-mode-toggle {
			background: var(--bg-l1);
		}
		.nested-group {
			background: var(--bg-lg);
		}
		.copy-category-card {
			background: var(--bg-lg);
		}
		.nested-card {
			background: #fff;
		}
		.group-name,
		.nested-card-title,
		.copy-group-title,
		.section-title {
			color: var(--txt-dg);
		}
		.admin-tab {
			background: var(--bg-l1);
			color: var(--txt-dg);
		}
		.copy-group-meta,
		.section-count {
			background: var(--bg-l1);
			color: var(--txt-dg);
		}
	}

	@media screen and (max-width: 900px) {
		flex-direction: column;
		padding: 88px 16px 60px 16px;

		.admin-nav {
			position: static;
			flex: 1 1 auto;
			width: 100%;
			flex-direction: row;
			flex-wrap: wrap;
		}
		.admin-panel {
			width: 100%;
		}
	}
`;
